<template>
  <!-- App Bar (Header) -->
  <v-app-bar :color="headerColor" prominent>
    <v-app-bar-nav-icon variant="text" @click.stop="drawer = !drawer" />
    <v-img src="/schwarte-jansky.svg" max-height="40" max-width="180" class="" />
    <v-spacer />

    <!-- User Menu -->
    <v-menu offset-y>
      <template #activator="{ props }">
        <v-btn v-bind="props" class="text-none" stacked>
          <v-avatar :color="avatarColor">
            <span>{{ userInitials }}</span>
          </v-avatar>
        </v-btn>
      </template>
      <v-list density="compact">
        <v-list-item :title="authStore.fullName" :subtitle="authStore.user?.email">
          <template #prepend>
            <v-avatar :color="avatarColor">
              <span>{{ userInitials }}</span>
            </v-avatar>
          </template>
        </v-list-item>
        <v-divider class="my-2" />
        <v-list-item
          :title="$t('views.settings.title')"
          prepend-icon="mdi-cog"
          to="/settings"
        />
        <v-list-item
          :title="$t('layouts.defaultLayout.navSignout')"
          prepend-icon="mdi-logout"
          @click="handleLogout"
        />
      </v-list>
    </v-menu>
  </v-app-bar>

  <!-- Navigation Drawer (Sidebar) -->
  <v-navigation-drawer v-model="drawer" floating app>
    <v-list nav density="compact">
      <template v-for="(item, index) in mainNavigation" :key="`main-${index}`">
        <!-- Divider -->
        <v-divider v-if="'divider' in item" class="my-2" />

        <!-- Single Item -->
        <v-list-item
          v-else-if="'to' in item"
          :prepend-icon="item.icon"
          :title="$t(item.title)"
          :to="item.to"
          exact
        />

        <!-- Item Group -->
        <v-list-group v-else-if="'items' in item" :value="item.title">
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              :prepend-icon="item.icon"
              :title="$t(item.title)"
            />
          </template>
          <v-list-item
            v-for="subItem in item.items"
            :key="subItem.title"
            :prepend-icon="subItem.icon"
            :title="$t(subItem.title)"
            :to="subItem.to"
            exact
          />
        </v-list-group>
      </template>
    </v-list>

    <template #append>
      <v-list nav density="compact">
        <template v-for="(item, index) in secondaryNavigation" :key="`sec-${index}`">
          <!-- Divider -->
          <v-divider v-if="'divider' in item" class="my-2" />
          <!-- Single Item -->
          <v-list-item
            v-else-if="'to' in item"
            :prepend-icon="item.icon"
            :title="$t(item.title)"
            :to="item.to"
            exact
            v-if="item.to !== '/control-panel' || authStore.isHomeApiAdmin"
          />
        </template>
      </v-list>
    </template>
  </v-navigation-drawer>

  <!-- Main Content Area -->
  <v-main>
    <router-view />
  </v-main>
</template>

<script setup lang="ts">
/**
 * This is the main layout for authenticated users. It includes the
 * top app bar, the navigation drawer (sidebar), and the main content area
 * where the router-view is displayed.
 *
 * @author Alexander Funke
 * @lastChange 2026-07-10
 */

// --- Imports ---
import { ref, computed } from 'vue';
import { mainNavigation, secondaryNavigation } from '@/config/navigation';
import { useAuthStore } from '@/stores/auth.store';
import { ADMIN_EMPLOYEE_UUID } from '@/config/constants';

// --- Store ---
const authStore = useAuthStore();

// --- State ---
const drawer = ref(true);

// --- Computed Properties ---
/**
 * Generates the user's initials from their name for display in avatars.
 * @returns {string} The user's initials (e.g., "JD" for "John Doe").
 */
const userInitials = computed(() => {
  const user = authStore.user;
  if (!user) return '??';
  const firstInitial = user.names ? user.names[0] : '';
  const lastInitial = user.surname ? user.surname[0] : '';
  return `${firstInitial}${lastInitial}`.toUpperCase();
});

// --- Methods ---
/**
 * Determines the header color based on the user's email.
 *
 * @returns {string} 'error' for the admin, 'primary' for others.
 *
 * @author Alexander Funke
 * @lastChange 2025-10-20
 */
const headerColor = computed(() => {
  if (authStore.user?.ID_employees === ADMIN_EMPLOYEE_UUID) {
    return 'red-darken-1';
  }
  return 'primary';
});

/**
 * Determines the avatar color based on the user's email.
 *
 * @returns {string} 'error' for the admin, 'info' for others.
 *
 * @author Alexander Funke
 * @lastChange 2025-10-20
 */
const avatarColor = computed(() => {
  if (authStore.user?.ID_employees === ADMIN_EMPLOYEE_UUID) {
    return 'error';
  }
  return 'info';
});

/**
 * Handles the logout action by calling the auth store's logout method.
 *
 * @author Alexander Funke
 * @lastChange 2025-09-15
 */
const handleLogout = () => {
  authStore.logout();
};
</script>

<style scoped>
.v-list-group__items .v-list-item {
  padding-inline-start: 32px !important;
}
</style>
